const MAX_VOICES = 64;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function smooth(param, value, at, seconds = 0.12) {
  if (param.cancelAndHoldAtTime) param.cancelAndHoldAtTime(at);
  else {
    const held = param.value;
    param.cancelScheduledValues(at);
    param.setValueAtTime(held, at);
  }
  param.linearRampToValueAtTime(value, at + seconds);
}

/** Owns only music nodes. Never resumes, suspends, or closes the caller's context. */
export function createRenderer(context, destination = context.destination) {
  const master = context.createGain();
  master.gain.value = 0;
  const compressor = context.createDynamicsCompressor();
  compressor.threshold.value = -12;
  compressor.knee.value = 6;
  compressor.ratio.value = 20;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.15;
  // A bounded transfer curve catches peaks that the compressor's attack lets through.
  const limiter = context.createWaveShaper();
  limiter.curve = Float32Array.from({ length: 2049 }, (_, i) => 0.85 * Math.tanh((i / 1024 - 1) * 1.4));
  limiter.oversample = 'none';
  master.connect(compressor);
  compressor.connect(limiter);
  limiter.connect(destination);
  smooth(master.gain, 0.45, context.currentTime, 0.25);

  const noise = context.createBuffer(1, context.sampleRate, context.sampleRate);
  const data = noise.getChannelData(0);
  let seed = 7391;
  for (let i = 0; i < data.length; i += 1) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    data[i] = seed / 2147483648 - 1;
  }
  const channels = new Set();
  const voices = new Set();
  let disposed = false;

  function releaseVoice(voice) {
    if (!voices.delete(voice)) return;
    voice.source.onended = null;
    voice.source.disconnect();
    voice.filter.disconnect();
    voice.envelope.disconnect();
  }

  function createChannel(pattern, at, fade = 0.12) {
    const gain = context.createGain();
    gain.gain.setValueAtTime(0, context.currentTime);
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(1, at + fade);
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 3200;
    filter.Q.value = 0.35;
    const channel = { gain, filter, layers: {}, pattern, stopAt: Infinity };
    for (const [id, definition] of Object.entries(pattern.layers)) {
      const node = context.createGain();
      node.gain.value = definition.gain;
      node.connect(filter);
      channel.layers[id] = node;
    }
    filter.connect(gain);
    gain.connect(master);
    channels.add(channel);
    return channel;
  }

  function mix(channel, intensity, era, at = context.currentTime) {
    Object.entries(channel.pattern.layers).forEach(([id, definition], i) => {
      const amount = definition.threshold === 0 ? 1
        : clamp((intensity - definition.threshold) / 0.3, 0, 1);
      smooth(channel.layers[id].gain, amount * definition.gain * (era.layers[i] ?? 1), at, 0.4);
    });
    smooth(channel.filter.frequency, era.cutoff, at, 0.6);
  }

  function schedule(channel, event) {
    if (disposed || voices.size >= MAX_VOICES || event.time >= channel.stopAt) return;
    const instrument = channel.pattern.layers[event.layer].instrument;
    const at = Math.max(context.currentTime, event.time);
    const duration = Math.max(0.03, event.duration);
    const release = instrument === 'pad' ? 0.3 : instrument === 'bell' ? 0.22 : 0.06;
    const end = at + duration + release;
    const source = instrument === 'percussion' ? context.createBufferSource() : context.createOscillator();
    const filter = context.createBiquadFilter();
    const envelope = context.createGain();
    filter.type = 'lowpass';
    filter.Q.value = 0.45;
    if (instrument === 'percussion') {
      source.buffer = noise;
      source.loop = true;
      filter.type = 'bandpass';
      filter.frequency.value = event.pitch < 55 ? 260 : 1500;
    } else {
      source.type = instrument === 'brass' ? 'sawtooth' : instrument === 'bell' ? 'sine' : 'triangle';
      source.frequency.setValueAtTime(440 * 2 ** ((event.pitch - 69) / 12), at);
      const cutoff = instrument === 'brass' ? 1100 : instrument === 'pad' ? 900 : 4200;
      filter.frequency.setValueAtTime(cutoff, at);
      if (instrument === 'pluck') filter.frequency.exponentialRampToValueAtTime(600, at + duration);
      if (instrument === 'bell') source.frequency.exponentialRampToValueAtTime(440 * 2 ** ((event.pitch - 69) / 12) * 0.998, end);
    }
    const peak = clamp(event.velocity, 0, 1) * (instrument === 'pad' ? 0.13 : 0.2);
    const attack = Math.min(duration * 0.3, instrument === 'pad' ? 0.15 : 0.012);
    envelope.gain.setValueAtTime(0, at);
    envelope.gain.linearRampToValueAtTime(peak, at + attack);
    envelope.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak * (instrument === 'pad' ? 0.75 : 0.14)), at + duration);
    envelope.gain.linearRampToValueAtTime(0, end);
    source.connect(filter);
    filter.connect(envelope);
    envelope.connect(channel.layers[event.layer]);
    const voice = { source, filter, envelope, channel };
    voices.add(voice);
    source.onended = () => releaseVoice(voice);
    source.start(at);
    source.stop(end);
  }

  function fadeOut(channel, at, duration) {
    smooth(channel.gain.gain, 0, at, duration);
    channel.stopAt = at + duration;
  }

  function collect(now) {
    for (const channel of channels) {
      if (channel.stopAt > now) continue;
      for (const voice of voices) {
        if (voice.channel === channel) {
          voice.source.stop();
          releaseVoice(voice);
        }
      }
      Object.values(channel.layers).forEach(node => node.disconnect());
      channel.filter.disconnect();
      channel.gain.disconnect();
      channels.delete(channel);
    }
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    const at = context.currentTime;
    smooth(master.gain, 0, at, 0.025);
    for (const voice of voices) voice.source.stop(at + 0.03);
    const disconnect = () => {
      for (const channel of channels) channel.stopAt = 0;
      collect(Infinity);
      master.disconnect();
      compressor.disconnect();
      limiter.disconnect();
    };
    if (context.state === 'running') setTimeout(disconnect, 50);
    else disconnect();
  }

  return {
    createChannel, mix, schedule, fadeOut, collect, dispose,
    setMuted(muted) { if (!disposed) smooth(master.gain, muted ? 0 : 0.45, context.currentTime); },
  };
}
